import {CuePoint} from '@playkit-js/common';

export enum CaptionAssetUsage {
  AAD = '1',
  CAPTION = '0'
}

type AADCuePointMetadataType = 'AADCuePointMetadataType';
const AAD_CUE_POINT_METADATA_TYPE_VALUE = 'AADCuePointMetadataType';

interface AADCuePointMetadata {
  identifier: string | undefined;
  text: string;
  styles: string;
  type: AADCuePointMetadataType;
  language?: string;
  isExternal?: boolean;
}

interface VttObject {
  cues?: Array<CuePoint<AADCuePointMetadata>>;
  valid: boolean;
}

class Parser {
  private static TIMESTAMP_REGX = /([0-9]{1,2})?:?([0-9]{2}):([0-9]{2}\.[0-9]{3})/;

  static parse(vttTest: string): VttObject {
    if (typeof vttTest !== 'string') {
      throw new Error('Parser::parse Input must be a string');
    }

    vttTest = vttTest.replace(/\r\n/g, '\n');
    vttTest = vttTest.replace(/\r/g, '\n');

    const parts = vttTest.split('\n\n');

    const header: string | undefined = parts.shift();

    if (!header || !header.startsWith('WEBVTT')) {
      throw new Error('Parser::parse - vttText must start with "WEBVTT"');
    }

    const headerParts = header.split('\n');

    // Don't parse if file contains only header.
    if (parts.length === 0 && headerParts.length === 1) {
      return {valid: true};
    }

    const cues: Array<CuePoint<AADCuePointMetadata>> = Parser.parseCues(parts);

    return {valid: true, cues};
  }

  private static parseCues(cues: string[]): Array<CuePoint<AADCuePointMetadata>> {
    const newCues: Array<CuePoint<AADCuePointMetadata>> = [];

    cues.forEach((cue, i) => {
      const parsedCue = Parser.parseCue(cue, i);
      if (parsedCue) {
        newCues.push(parsedCue);
      }
    });

    return newCues;
  }

  private static parseCue(cueString: string, i: number) {
    const cue: CuePoint<AADCuePointMetadata> = {
      id: i.toString(),
      startTime: 0,
      endTime: 0,
      type: '',
      metadata: {type: AAD_CUE_POINT_METADATA_TYPE_VALUE, identifier: '', text: '', styles: ''}
    };

    // split and remove empty lines
    let lines: string[] = cueString.split('\n').filter(Boolean);

    if (!lines.length || lines[0].startsWith('NOTE')) {
      return null;
    }

    if (lines.length === 1 && !lines[0].includes('-->')) {
      throw new Error(`Parser::parseCue time identifier (-->) can't be standalone (cue #${i})`);
    }

    if (lines.length > 1 && !(lines[0].includes('-->') || lines[1].includes('-->'))) {
      const msg = `Parser::parseCue time identifier (-->) needs to be followed by timestamp (cue #${i})`;
      throw new Error(msg);
    }

    if (lines.length > 1 && lines[1].includes('-->')) {
      cue.metadata.identifier = lines.shift();
    }

    if (lines.length > 0 && lines[0].includes('-->')) {
      const times = lines[0].split(' --> ');

      if (times.length !== 2 || !Parser.validTimestamp(times[0]) || !Parser.validTimestamp(times[1])) {
        throw new Error(`Parser::parseCue Invalid cue timestamp (cue #${i})`);
      }

      cue.startTime = Parser.parseTimestamp(times[0]);

      /**
       * Make sure that the audio description text is read only once, just at the start time.
       * Some player plugins / services (like the `platkit-navigation` plugin) cause the `TIMED_METADATA_CHANGE` event to be emitted multiple times.
       */
      cue.endTime = Parser.parseTimestamp(times[1]);
      cue.metadata.styles = times[1].replace(Parser.TIMESTAMP_REGX, '').trim();

      lines.shift();
    }

    cue.metadata.text = lines.join(' ');

    return cue;
  }

  private static validTimestamp(timestamp: string) {
    return Parser.TIMESTAMP_REGX.test(timestamp);
  }

  private static parseTimestamp(timestamp: string): number {
    const matches = timestamp.match(Parser.TIMESTAMP_REGX);
    if (matches === null) {
      return 0.0;
    }

    let secs = parseFloat(matches[3] ?? 0);
    secs += parseFloat(matches[2] ?? 0) * 60; // mins
    secs += parseFloat(matches[1] ?? 0) * 60 * 60; // hours

    return secs;
  }
}

export {Parser, AADCuePointMetadata, VttObject, AADCuePointMetadataType, AAD_CUE_POINT_METADATA_TYPE_VALUE};
